import { useState, useCallback, useMemo } from 'react'
import { satsueiGuideService } from '../../web/services/satsueiGuideService'
import { useApiError } from '../../shared/hooks/useApiError'

export const useSatsueiGuide = () => {
    // Main state
    const [categories, setCategories] = useState([])
    const [selectedCategoryId, setSelectedCategoryId] = useState(0)
    const [guides, setGuides] = useState([])
    const [deletedGuides, setDeletedGuides] = useState([])
    const [selectedGuideId, setSelectedGuideId] = useState(0)
    const [guideDetails, setGuideDetails] = useState([])
    const [deletedGuideDetails, setDeletedGuideDetails] = useState([])

    // Loading states
    const [isLoading, setIsLoading] = useState(false)
    const [isUploading, setIsUploading] = useState(false)

    // Helper functions
    const getMaxGuideId = useCallback(() => {
        const allGuides = [...guides, ...deletedGuides]
        return Math.max(...allGuides.map(g => parseInt(g.satsueiGuideId) || 0), 0)
    }, [guides, deletedGuides])

    const getMaxGuideDetailId = useCallback(() => {
        const allDetails = [...guideDetails, ...deletedGuideDetails]
        return Math.max(...allDetails.map(d => parseInt(d.satsueiGuideDetailId) || 0), 0)
    }, [guideDetails, deletedGuideDetails])

    // Data operations
    const loadCategories = useCallback(async () => {
        try {
            setIsLoading(true)
            const response = await satsueiGuideService.getSatsueiMemoCategory()
            setCategories(response.satsueiMemoCategoryList || [])
        } catch (error) {
            console.error('Failed to load categories:', error)
            throw error
        } finally {
            setIsLoading(false)
        }
    }, [])

    const { handleApiResponse } = useApiError()

    const loadGuides = useCallback(async (categoryId) => {
        if (categoryId === undefined || categoryId === null) {
            setGuides([])
            setDeletedGuides([])
            return
        }

        try {
            setIsLoading(true)
            const response = await satsueiGuideService.getSatsueiGuideList(categoryId)
            const guideList = response.satsueiGuideList || []

            // Mark all loaded guides as existing (they came from the database)
            const activeGuides = guideList.filter(g => parseInt(g.deleteFlg) === 0).map((g, index) => ({
                ...g,
                isExisting: true,
                isNew: false,
                isModified: false, // Explicitly set to false for loaded data
                orderIndex: index + 1, // Add order index
                originalData: { ...g } // Keep original data for comparison
            }))
            const deletedGuideList = guideList.filter(g => parseInt(g.deleteFlg) === 1).map(g => ({
                ...g,
                isExisting: true,
                isNew: false,
                isModified: false, // Explicitly set to false for loaded data
                originalData: { ...g }
            }))

            setGuides(activeGuides)
            setDeletedGuides(deletedGuideList)
        } catch (error) {
            console.error('Failed to load guides:', error)
            throw error
        } finally {
            setIsLoading(false)
        }
    }, [])

    const loadGuideDetails = useCallback(async (categoryId, guideId) => {
        if (categoryId === undefined || categoryId === null || !guideId) {
            setGuideDetails([])
            setDeletedGuideDetails([])
            return
        }

        try {
            setIsLoading(true)
            const response = await satsueiGuideService.getSatsueiGuideDetailList(categoryId, guideId)
            const detailList = response.satsueiGuideDetailList || []

            // Mark all loaded details as existing (they came from the database)
            const activeDetails = detailList.filter(d => parseInt(d.deleteFlg) === 0).map((d, index) => ({
                ...d,
                isExisting: true,
                isNew: false,
                isModified: false, // Explicitly set to false for loaded data
                orderIndex: index + 1, // Add order index
                originalData: { ...d } // Keep original data for comparison
            }))
            const deletedDetailList = detailList.filter(d => parseInt(d.deleteFlg) === 1).map(d => ({
                ...d,
                isExisting: true,
                isNew: false,
                isModified: false, // Explicitly set to false for loaded data
                originalData: { ...d }
            }))

            setGuideDetails(activeDetails)
            setDeletedGuideDetails(deletedDetailList)
        } catch (error) {
            console.error('Failed to load guide details:', error)
            throw error
        } finally {
            setIsLoading(false)
        }
    }, [])

    const addGuide = useCallback((guideName, setsumei) => {
        const maxId = getMaxGuideId()
        if (maxId >= 999) {
            throw new Error('撮影ガイドはこれ以上追加出来ません。')
        }

        const newGuide = {
            satsueiGuideId: (maxId + 1).toString(),
            satsueiGuideName: guideName,
            setsumei: setsumei,
            deleteFlg: '0',
            orderIndex: guides.length + 1, // Add to the end of the list
            isExisting: false,
            isNew: true // Mark as newly added
        }

        setGuides(prev => [...prev, newGuide])
        return newGuide
    }, [getMaxGuideId, guides.length])

    const updateGuide = useCallback((guideId, guideName, setsumei) => {
        setGuides(prev => prev.map(g =>
            g.satsueiGuideId === guideId
                ? {
                    ...g,
                    satsueiGuideName: guideName,
                    setsumei,
                    isModified: true // Mark as modified
                }
                : g
        ))
    }, [])

    const deleteGuide = useCallback((guideId) => {
        const guideToDelete = guides.find(g => g.satsueiGuideId === guideId)
        if (guideToDelete) {
            setGuides(prev => prev.filter(g => g.satsueiGuideId !== guideId))
            setDeletedGuides(prev => [...prev, { ...guideToDelete, deleteFlg: '1' }])
        }
    }, [guides])

    const restoreGuide = useCallback((guideId) => {
        const guideToRestore = deletedGuides.find(g => g.satsueiGuideId === guideId)
        if (guideToRestore) {
            setDeletedGuides(prev => prev.filter(g => g.satsueiGuideId !== guideId))
            setGuides(prev => [...prev, {
                ...guideToRestore,
                deleteFlg: '0',
                isModified: true // Mark as modified so it gets sent to update API
            }])
        }
    }, [deletedGuides])

    const addGuideDetail = useCallback(() => {
        const maxId = getMaxGuideDetailId()
        if (maxId >= 999) {
            throw new Error('ガイド画像はこれ以上追加出来ません。')
        }

        const newDetail = {
            satsueiGuideId: selectedGuideId,
            satsueiGuideDetailId: (maxId + 1).toString(),
            satsueiGuideDetailName: '',
            setsumei: '',
            filenameExtension: '',
            deleteFlg: '0',
            orderIndex: guideDetails.length + 1, // Add to the end of the list
            isExisting: false,
            isNew: true // Mark as newly added
        }

        setGuideDetails(prev => [...prev, newDetail])
        return newDetail
    }, [getMaxGuideDetailId, selectedGuideId, guideDetails.length])

    const updateGuideDetail = useCallback((detailId, detailName, setsumei) => {
        setGuideDetails(prev => prev.map(d =>
            d.satsueiGuideDetailId === detailId
                ? {
                    ...d,
                    satsueiGuideDetailName: detailName,
                    setsumei,
                    isModified: true // Mark as modified
                }
                : d
        ))
    }, [])

    const deleteGuideDetail = useCallback((detailId) => {
        const detailToDelete = guideDetails.find(d => d.satsueiGuideDetailId === detailId)
        if (detailToDelete) {
            setGuideDetails(prev => prev.filter(d => d.satsueiGuideDetailId !== detailId))
            setDeletedGuideDetails(prev => [...prev, { ...detailToDelete, deleteFlg: '1' }])
        }
    }, [guideDetails])

    const restoreGuideDetail = useCallback((detailId) => {
        const detailToRestore = deletedGuideDetails.find(d => d.satsueiGuideDetailId === detailId)
        if (detailToRestore) {
            setDeletedGuideDetails(prev => prev.filter(d => d.satsueiGuideDetailId !== detailId))
            setGuideDetails(prev => [...prev, {
                ...detailToRestore,
                deleteFlg: '0',
                isModified: true // Mark as modified so it gets sent to update API
            }])
        }
    }, [deletedGuideDetails])

    const uploadImage = useCallback(async (guideId, detailId, file) => {
        try {
            setIsUploading(true)

            const formData = new FormData()
            formData.append('inputFile', file)
            formData.append('fileName', file.name)
            formData.append('fileSize', file.size)
            formData.append('satsueiMemoCategoryId', selectedCategoryId)
            formData.append('satsueiGuideId', guideId)
            formData.append('satsueiGuideDetailId', detailId)

            const response = await satsueiGuideService.uploadSatsueiGuideImage(formData)
            if (handleApiResponse(response)) {
                return // Error was handled, exit early
            }

            // Update the detail with the new filename extension
            const filenameExtension = file.name.substring(file.name.lastIndexOf('.') + 1)
            setGuideDetails(prev => prev.map(d =>
                d.satsueiGuideId === guideId && d.satsueiGuideDetailId === detailId
                    ? { ...d, filenameExtension, isModified: true } // Mark as modified when image is uploaded
                    : d
            ))

            return response
        } catch (error) {
            console.error('Failed to upload image:', error)
            throw error
        } finally {
            setIsUploading(false)
        }
    }, [selectedCategoryId])

    const saveAll = useCallback(async () => {
        try {
            setIsLoading(true)

            // Prepare guide data by operation type
            // New guides are those that were added in this session (isNew: true)
            const newGuides = guides.filter(g => g.isNew).map(g => ({
                satsueiMemoCategoryId: selectedCategoryId,
                satsueiGuideId: g.satsueiGuideId,
                satsueiGuideName: g.satsueiGuideName,
                setsumei: g.setsumei,
                orderIndex: g.orderIndex
            }))

            // Updated guides are existing guides that have been modified
            const updatedGuides = guides.filter(g => g.isExisting && !g.isNew && g.isModified).map(g => ({
                satsueiMemoCategoryId: selectedCategoryId,
                satsueiGuideId: g.satsueiGuideId,
                satsueiGuideName: g.satsueiGuideName,
                setsumei: g.setsumei,
                orderIndex: g.orderIndex
            }))

            const removedGuides = deletedGuides.map(g => ({
                satsueiMemoCategoryId: selectedCategoryId,
                satsueiGuideId: g.satsueiGuideId
            }))

            // Prepare detail data by operation type
            const newDetails = guideDetails.filter(d => d.isNew).map(d => ({
                satsueiMemoCategoryId: selectedCategoryId,
                satsueiGuideId: d.satsueiGuideId,
                satsueiGuideDetailId: d.satsueiGuideDetailId,
                satsueiGuideDetailName: d.satsueiGuideDetailName,
                setsumei: d.setsumei,
                orderIndex: d.orderIndex
            }))

            const updatedDetails = guideDetails.filter(d => d.isExisting && !d.isNew && d.isModified).map(d => ({
                satsueiMemoCategoryId: selectedCategoryId,
                satsueiGuideId: d.satsueiGuideId,
                satsueiGuideDetailId: d.satsueiGuideDetailId,
                satsueiGuideDetailName: d.satsueiGuideDetailName,
                setsumei: d.setsumei,
                orderIndex: d.orderIndex
            }))

            const removedDetails = deletedGuideDetails.map(d => ({
                satsueiMemoCategoryId: selectedCategoryId,
                satsueiGuideId: d.satsueiGuideId,
                satsueiGuideDetailId: d.satsueiGuideDetailId
            }))

            // Execute guide operations
            const guideResponses = []
            if (newGuides.length > 0) {
                const addResponse = await satsueiGuideService.addSatsueiGuideList(newGuides)
                if (handleApiResponse(addResponse)) {
                    return // Error was handled, exit early
                }
                guideResponses.push(addResponse)

                // Mark newly added guides as existing after successful save
                setGuides(prev => prev.map(g =>
                    g.isNew ? { ...g, isExisting: true, isNew: false, isModified: false } : g
                ))
            }

            if (updatedGuides.length > 0) {
                const updateResponse = await satsueiGuideService.updateSatsueiGuideList(updatedGuides)
                if (handleApiResponse(updateResponse)) {
                    return // Error was handled, exit early
                }
                guideResponses.push(updateResponse)

                // Reset modified flag for successfully updated guides
                setGuides(prev => prev.map(g =>
                    g.isModified ? { ...g, isModified: false } : g
                ))
            }

            if (removedGuides.length > 0) {
                const removeResponse = await satsueiGuideService.removeSatsueiGuideList(removedGuides)
                if (handleApiResponse(removeResponse)) {
                    return // Error was handled, exit early
                }
                guideResponses.push(removeResponse)
            }

            // Execute detail operations
            const detailResponses = []
            if (newDetails.length > 0) {
                const addDetailResponse = await satsueiGuideService.addSatsueiGuideDetailList(newDetails)
                if (handleApiResponse(addDetailResponse)) {
                    return // Error was handled, exit early
                }
                detailResponses.push(addDetailResponse)

                // Mark newly added details as existing after successful save
                setGuideDetails(prev => prev.map(d =>
                    d.isNew ? { ...d, isExisting: true, isNew: false, isModified: false } : d
                ))
            }

            if (updatedDetails.length > 0) {
                const updateDetailResponse = await satsueiGuideService.updateSatsueiGuideDetailList(updatedDetails)
                if (handleApiResponse(updateDetailResponse)) {
                    return // Error was handled, exit early
                }
                detailResponses.push(updateDetailResponse)

                // Reset modified flag for successfully updated details
                setGuideDetails(prev => prev.map(d =>
                    d.isModified ? { ...d, isModified: false } : d
                ))
            }

            if (removedDetails.length > 0) {
                const removeDetailResponse = await satsueiGuideService.removeSatsueiGuideDetailList(removedDetails)
                if (handleApiResponse(removeDetailResponse)) {
                    return // Error was handled, exit early
                }
                detailResponses.push(removeDetailResponse)
            }

            return { guideResponses, detailResponses }
        } catch (error) {
            console.error('Failed to save:', error)
            throw error
        } finally {
            setIsLoading(false)
        }
    }, [guides, deletedGuides, guideDetails, deletedGuideDetails, selectedCategoryId])

    const reorderGuides = useCallback((newOrder) => {
        // Add order index to each item and mark as modified if order changed
        const orderedList = newOrder.map((guide, index) => {
            const newOrderIndex = index + 1
            const isOrderChanged = guide.orderIndex !== newOrderIndex

            return {
                ...guide,
                orderIndex: newOrderIndex,
                isModified: guide.isModified || isOrderChanged // Mark as modified if order changed
            }
        })
        setGuides(orderedList)
    }, [])

    const reorderGuideDetails = useCallback((newOrder) => {
        // Add order index to each item and mark as modified if order changed
        const orderedList = newOrder.map((detail, index) => {
            const newOrderIndex = index + 1
            const isOrderChanged = detail.orderIndex !== newOrderIndex

            return {
                ...detail,
                orderIndex: newOrderIndex,
                isModified: detail.isModified || isOrderChanged // Mark as modified if order changed
            }
        })
        setGuideDetails(orderedList)
    }, [])

    // Computed values
    const selectedCategory = useMemo(() =>
        categories.find(c => c.satsueiMemoCategoryId === selectedCategoryId),
        [categories, selectedCategoryId]
    )

    const selectedGuide = useMemo(() =>
        guides.find(g => g.satsueiGuideId === selectedGuideId),
        [guides, selectedGuideId]
    )

    return {
        // State
        categories,
        selectedCategoryId,
        selectedCategory,
        guides,
        deletedGuides,
        selectedGuideId,
        selectedGuide,
        guideDetails,
        deletedGuideDetails,
        isLoading,
        isUploading,

        // Actions
        setSelectedCategoryId,
        setSelectedGuideId,
        loadCategories,
        loadGuides,
        loadGuideDetails,
        addGuide,
        updateGuide,
        deleteGuide,
        restoreGuide,
        addGuideDetail,
        updateGuideDetail,
        deleteGuideDetail,
        restoreGuideDetail,
        uploadImage,
        saveAll,
        reorderGuides,
        reorderGuideDetails
    }
}
